/** The tenant's shared Wi-Fi — SSID(s), password display/share, and the
 *  admin-only password change. Moved verbatim from `pages/network.tsx` when
 *  the page split into tabs (#310); the card renders on the Connect tab. */
import { useState } from "react";
import { QRCodeSVG } from "qrcode.react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { Copy, Eye, EyeOff, MonitorSmartphone } from "lucide-react";
import { toast } from "sonner";
import { Badge, Box, Button, Flex, Group, Modal, PasswordInput, Stack, Text, TextInput } from "@mantine/core";
import { tenantApi } from "@/lib/api/client";
import { WIRELESS_QUERY_KEY } from "@/hooks/use-network-queries";
import { copyToClipboard } from "./shared";

function wifiQrString(ssid: string, password: string): string {
  const esc = (s: string) => s.replace(/[\\;,":\s]/g, (c) => `\\${c}`);
  return `WIFI:T:WPA;S:${esc(ssid)};P:${esc(password)};;`;
}

function PasswordDisplay({ password }: { password: string }) {
  const [revealed, setRevealed] = useState(false);

  return (
    <Group gap="xs">
      <TextInput
        readOnly
        type={revealed ? "text" : "password"}
        value={password}
        style={{ flex: 1, fontFamily: "monospace" }}
      />
      <Button
        variant="outline"
        size="compact-sm"
        onClick={() => setRevealed((v) => !v)}
        aria-label={revealed ? "Hide password" : "Reveal password"}
      >
        {revealed ? <EyeOff size={16} /> : <Eye size={16} />}
      </Button>
      <Button variant="outline" size="compact-sm" onClick={() => copyToClipboard(password)} aria-label="Copy">
        <Copy size={16} />
      </Button>
    </Group>
  );
}

function SharePasswordModal({
  ssid,
  password,
  opened,
  onClose,
}: {
  ssid: string;
  password: string;
  opened: boolean;
  onClose: () => void;
}) {
  return (
    <Modal opened={opened} onClose={onClose} title="Connect to WiFi" size="sm">
      <Stack gap="md">
        <div>
          <Text fz="xs" c="dimmed" mb={4}>Network</Text>
          <Text fw={600}>{ssid}</Text>
        </div>

        <div>
          <Text fz="xs" c="dimmed" mb="xs">Password</Text>
          <Flex wrap="wrap" gap={4}>
            {password.split("").map((char, i) => (
              <Box
                key={i}
                style={{
                  display: "inline-flex",
                  alignItems: "center",
                  justifyContent: "center",
                  width: 32,
                  height: 40,
                  border: "1px solid var(--mantine-color-default-border)",
                  borderRadius: 4,
                  fontSize: 18,
                  fontFamily: "monospace",
                  fontWeight: 600,
                  background: "var(--mantine-color-default-hover)",
                }}
              >
                {char}
              </Box>
            ))}
          </Flex>
        </div>

        <Button variant="outline" size="sm" fullWidth onClick={() => copyToClipboard(password)}>
          <Copy size={12} style={{ marginRight: 4 }} />
          Copy password
        </Button>

        <Stack align="center" gap="xs">
          <Box p={12} style={{ background: "white", borderRadius: 8 }}>
            <QRCodeSVG value={wifiQrString(ssid, password)} size={180} />
          </Box>
          <Text fz="xs" c="dimmed">Scan to connect</Text>
        </Stack>
      </Stack>
    </Modal>
  );
}

export function WiFiNetworkCard({
  ssid,
  password,
  label,
  showPassword,
}: {
  ssid: string;
  password?: string | null;
  label: string;
  showPassword: boolean;
}) {
  const [shareOpen, setShareOpen] = useState(false);

  return (
    <Stack gap="sm">
      <Group justify="space-between">
        <Text fz="xs" fw={500} c="dimmed" tt="uppercase" style={{ letterSpacing: "0.05em" }}>{label}</Text>
        {password && showPassword && (
          <Button variant="subtle" size="compact-xs" onClick={() => setShareOpen(true)}>
            <MonitorSmartphone size={12} style={{ marginRight: 4 }} />
            Share
          </Button>
        )}
      </Group>

      <Badge variant="light" size="lg" style={{ padding: "4px 12px" }}>
        {ssid}
      </Badge>

      {password && showPassword ? (
        <PasswordDisplay password={password} />
      ) : password && !showPassword ? (
        <Text fz="xs" c="dimmed">Password visible to admins only</Text>
      ) : (
        <Text fz="xs" c="dimmed">No password required</Text>
      )}

      {password && showPassword && (
        <SharePasswordModal ssid={ssid} password={password} opened={shareOpen} onClose={() => setShareOpen(false)} />
      )}
    </Stack>
  );
}

/**
 * Queue a new password for the TENANT's shared Wi-Fi (tenant admin only —
 * the backend enforces it too, 403 without profile.tenant_admin).
 *
 * Lifted from the former /wifi page, which has redirected to /network since
 * 4272ea8f and was imported by nothing, so this control was unreachable.
 * It belongs beside the network's own password, not on My Account: it
 * changes the key the whole company shares, not a personal credential.
 */
export function ChangeWifiPassword({ onChanged }: { onChanged?: () => void } = {}) {
  const qc = useQueryClient();
  const [value, setValue] = useState("");
  const mutation = useMutation({
    mutationFn: (password: string) => tenantApi.changeWirelessPassword(password),
    onSuccess: () => {
      toast.success("New password queued — it will apply overnight");
      setValue("");
      qc.invalidateQueries({ queryKey: WIRELESS_QUERY_KEY });
      onChanged?.();
    },
    onError: () => toast.error("Failed to queue password change"),
  });

  const tooShort = value.length > 0 && value.length < 8;

  return (
    <Stack gap={4}>
      <Text fz="sm" c="dimmed">Change password (applies overnight)</Text>
      <Group gap="xs" wrap="nowrap" align="flex-start">
        <PasswordInput
          flex={1}
          placeholder="New password (8–63 characters)"
          value={value}
          onChange={(e) => setValue(e.currentTarget.value)}
          error={tooShort ? "At least 8 characters" : undefined}
        />
        <Button
          onClick={() => mutation.mutate(value)}
          loading={mutation.isPending}
          disabled={value.length < 8 || value.length > 63}
        >
          Queue
        </Button>
      </Group>
    </Stack>
  );
}
